import type { AccountMeetingLocationModel } from './account.type.ts';
import type { FrequencyEnum } from './account-category.type.ts';
import type { AccountRepresentativeListModel } from './account-representative.type.ts';
import type { AvailabilitySlotListModel } from './availability-slot.type.ts';
import type {
  ConfirmationStatusEnum,
  ConfirmationStatusModel,
} from './confirmation-status.model.ts';
import type { RepresentativeStatusEnum } from './representative-status.type.ts';
import type { StaffMemberListModel } from './staff-member.type.ts';
import type { UserListModel } from './user-type.ts';

export interface MeetingModel {
  key: string;
  id: number;
  start: string;
  end: string;
  foodCategoryId?: number;
  foodCategory?: string;
  menu?: string;
  drugName?: string;
  notes?: string;
  officeNotes?: string;
  accountRepresentative: AccountRepresentativeListModel;
  accountMeetingLocation: AccountMeetingLocationModel;
  availabilitySlot?: AvailabilitySlotListModel;
  meetingAttendees: MeetingAttendeeModel[];
  meetingMedias: MeetingMediaModel[];
  meetingStatuses: MeetingStatusModel[];
}

export interface MeetingAttendeeModel {
  id: number;
  staffMember: StaffMemberListModel;
  meetingId: number;
  confirmationStatus: ConfirmationStatusModel;
}

export interface MeetingMediaModel {
  id: number;
  meetingId: number;
  uploadedByUserId: number;
  uploadedAt: string;
  mimeType: string;
  fileName: string;
}

export interface MeetingStatusModel {
  id: number;
  setAt: string;
  notes: string;
  meetingId: number;
  user: UserListModel;
  meetingStatusType: MeetingStatusTypeModel;
}

export interface MeetingStatusTypeModel {
  id: MeetingStatusTypeEnum;
  description: string;
}

export enum MeetingStatusTypeEnum {
  Requested = 1,
  Approved = 2,
  Canceled = 3,
}

export interface MeetingFormModel extends Omit<MeetingModel, 'foodCategory'> {
  date: string;
}

export interface MeetingListModel {
  key: string;
  id: number;
  start: string;
  end: string;
  foodCategory?: string;
  foodCategoryConfirmedAt?: string;
  menu?: string;
  drugName: string;
  notes?: string;
  officeNotes?: string;
  accountMeetingLocationDescription?: string;
  accountRepresentativeStatusId?: RepresentativeStatusEnum;
  representativeUserId: number;
  representativeFirstName: string;
  representativeLastName: string;
  representativeEmailAddress: string;
  representativeCompany: string;
  accountCategoryFrequencyId?: FrequencyEnum;
  frequencyOverrideId?: FrequencyEnum;
  representativeProfilePhotoUrl?: string;
  accountId: number;
  accountKey: string;
  accountName: string;
  meetingStatusTypeId?: MeetingStatusTypeEnum;
  meetingStatusDescription?: string;
  meetingStatusUpdated?: string;
}

export interface MeetingRequestModel {
  availabilitySlotId: number;
  foodCategoryId: number;
  menu?: string;
  drugName: string;
  notes: string;
  mediaFileNames: string[];
  requestedAttendeeUserIds: number[];
}

export interface MeetingUpdateModel {
  foodCategoryId: number;
  menu?: string;
  drugName: string;
  notes: string;
  mediaFileNames: string[];
  requestedAttendeeUserIds: number[];
}

export interface UpdateMeetingAttendeeStatusModel {
  staffMemberUserId: number;
  confirmationStatus: ConfirmationStatusEnum;
}

export interface MeetingCancelModel {
  notes: string;
}

export interface MeetingRescheduleModel {
  availabilitySlotId: number | undefined;
  accountMeetingLocationId: number | undefined;
  start: string;
  end: string;
  notes: string;
}

export interface MeetingWithFoodCategoryModel {
  key: string;
  start: string;
  end: string;
  foodCategoryId: number;
  foodCategory: string;
}

export interface ConfirmMeetingFoodCategoryModel {
  foodCategoryId: number;
  menu?: string;
}
